import React from 'react';
import { twMerge } from 'tailwind-merge';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';

interface StepIndicatorProps {
  steps: string[];
  activeStep: number;
  /** Can be a boolean (all steps completed) or Record<number, boolean> for per-step completion */
  completed: boolean | Record<number, boolean>;
  handleStep: (index: number) => void;
}

const StepIndicator: React.FC<StepIndicatorProps> = ({
  steps,
  activeStep,
  completed,
  handleStep,
}) => {
  /**
   * Determine if a step is completed
   * - If completed is a boolean: all steps before activeStep are completed, or all steps if true
   * - If completed is a Record: check the specific index
   */
  const isStepCompleted = (index: number): boolean => {
    if (typeof completed === 'boolean') {
      // If boolean true, all steps are completed; otherwise, steps before active are completed
      return completed ? true : index < activeStep;
    }
    return completed[index] ?? false;
  };

  return (
    <div className="flex w-full">
      {steps.map((label, index) => {
        const isCompleted = isStepCompleted(index);
        const isPrevCompleted = index > 0 && isStepCompleted(index - 1);
        const isActive = index === activeStep;
        const isFirst = index === 0;
        const isLast = index === steps.length - 1;

        return (
          <div key={label} className="flex-1 relative">
            {/* Left connector line (skip first step) */}
            {!isFirst && (
              <div
                className={twMerge(
                  'absolute top-3 left-0 right-1/2 h-px mr-3',
                  isPrevCompleted ? 'bg-sky-500' : 'bg-neutral-300'
                )}
              />
            )}
            {/* Right connector line (skip last step) */}
            {!isLast && (
              <div
                className={twMerge(
                  'absolute top-3 left-1/2 right-0 h-px ml-3',
                  isCompleted ? 'bg-sky-500' : 'bg-neutral-300'
                )}
              />
            )}
            {/* Step button (icon + label) */}
            <button
              type="button"
              onClick={() => handleStep(index)}
              className="relative z-10 flex flex-col items-center w-full"
            >
              <div
                className={twMerge(
                  'flex items-center justify-center w-6 h-6 rounded-full border transition-colors',
                  isCompleted
                    ? 'bg-sky-500 border-sky-500 text-white'
                    : isActive
                      ? 'border-sky-500 text-sky-500 bg-white'
                      : 'border-neutral-300 text-neutral-400 bg-white'
                )}
              >
                {isCompleted ? (
                  <Icon icon={faCheck} className="text-[10px]" />
                ) : (
                  <span className="text-xs">{index + 1}</span>
                )}
              </div>
              <span
                className={twMerge(
                  'mt-1 text-xs transition-colors whitespace-nowrap',
                  isActive
                    ? 'text-sky-500 font-medium'
                    : 'text-neutral-500'
                )}
              >
                {label}
              </span>
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default StepIndicator;
